import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { FC, useEffect, useState } from "react";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { PageIconHeader } from "@hostingbv/lib/src/components/PageIconHeader";
import { Callout } from "../../../lib/src/components/content/Callout";
import { FilterableDataTable } from "@ventumsoftware/react-toolkit";
import { Input } from "@hostingbv/lib/src/components/form/Input";
import { Button, IconButton } from "../../../lib/src/components/form/Button";
import { Deployment } from "../../../lib/src/api/partner/models/Deployment";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { DeploymentStatusLabel } from "../../../lib/src/components/DeploymentStatusLabel";
import { useModal } from "../../../lib/src/components/layout/ModalProvider";
import { DeploymentDetailsModal } from "../modals/DeploymentDetailsModal";
import { faClockRotateLeft, faRefresh } from "@fortawesome/free-solid-svg-icons";

export const DeploymentLog: FC = () => {
  const DAY = 1000 * 3600 * 24;
  const [from, setFrom] = useState(new Date(Date.now() - DAY * 30));
  const [to, setTo] = useState(new Date(Date.now() + DAY));
  const { partnerClient } = useApiClient(config);
  const [resource, setResource] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(false);
  const { deployments } = useRealtimeDeployments()!;

  const loadDeployments = async () => {
    setLoading(true);
    const d = await partnerClient.getDeployments(from, to);
    if (d.success) {
      setResource(d.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (from.getTime() > to.getTime()) {
      setTo(new Date(from.getTime() + 3600 * 1000));
    } else {
      // Range is valid, reload deployments
      loadDeployments();
    }
  }, [from, to]);

  useRefreshEffect(() => {
    loadDeployments();
  });

  const allDeployments: Deployment[] = Object.values(
    [...resource, ...deployments].reduce(
      (map, d) => {
        map[d.id] = d;
        return map;
      },
      {} as { [key: string]: Deployment },
    ),
  );
  const sortedDeployments = allDeployments.sort((a, b) => (a.created > b.created ? -1 : 1));
  return (
    <ContentContainer size={"lg"}>
      <PageIconHeader icon={faClockRotateLeft}>
        <h1 className={"h-12 flex items-center text-2xl -mt-6 mb-4 font-bold text-test"}>Deployment log</h1>
      </PageIconHeader>
      <Callout>
        <SectionHeader>Organization deployment history</SectionHeader>
        <Paragraph>
          In this log, you can view a history of what has happened to your resources. Even if a customer with access to
          them has changed something, you can see it here.
        </Paragraph>
      </Callout>
      <div className={"flex items-end space-x-4 mb-4"}>
        <div className={"w-48"}>
          <Input type={"date"} label={"From"} value={from} onChange={setFrom} />
        </div>
        <div className={"w-48"}>
          <Input type={"date"} label={"To"} value={to} onChange={setTo} />
        </div>
        <Button
          text={"Last 30 days"}
          type={"secondary"}
          size={"sm"}
          onClick={() => {
            setFrom(new Date(Date.now() - DAY * 30));
            setTo(new Date(Date.now() + DAY));
          }}
        />
        <Button
          text={"Last 90 days"}
          type={"secondary"}
          size={"sm"}
          onClick={() => {
            setFrom(new Date(Date.now() - DAY * 90));
            setTo(new Date(Date.now() + DAY));
          }}
        />
        <Button
          text={"Last year"}
          type={"secondary"}
          size={"sm"}
          onClick={() => {
            setFrom(new Date(Date.now() - DAY * 365));
            setTo(new Date(Date.now() + DAY));
          }}
        />
      </div>
      <FilterableDataTable
        actions={<IconButton icon={faRefresh} onClick={() => loadDeployments()} type={"secondary"} size={"sm"} />}
        columns={[
          { header: "Name", property: "entityName" },
          {
            header: "Deployment",
            property: "type",
            transform: (t, d) => (
              <div>
                <DeploymentStatusLabel status={d.status} />
                <span className={"text-sm text-slate-600"}>{t}</span>
              </div>
            ),
          },
          { header: "User", property: "initiatedName" },
          {
            header: "Date",
            property: "started",
            transform: (d) =>
              d === null ? (
                <></>
              ) : (
                <span>
                  {d.toLocaleDateString("nl")} {d.toLocaleTimeString()}
                </span>
              ),
          },
          {
            header: "Action",
            property: "id",
            transform: (i, d) => {
              return <DeploymentDetailsButton deployment={d} />;
            },
          },
        ]}
        data={sortedDeployments}
        keyProperty={"id"}
        loading={loading}
      />
    </ContentContainer>
  );
};
const DeploymentDetailsButton = (props: { deployment: Deployment }) => {
  const { open } = useModal({
    title: "Deployment details",
    size: "lg",
    body: <DeploymentDetailsModal deployment={props.deployment} />,
  });
  return (
    <div>
      <Button type={"secondary"} size={"xs"} text={"Details"} onClick={() => open()} />
    </div>
  );
};
